import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { z } from "zod";

const SITE_ORIGIN = "https://kupish.si";

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ email: z.string().email("Vnesite veljaven email naslov.") }).parse(data)
  )
  .handler(async ({ data }) => {
    const { rateLimit } = await import("@/lib/rate-limit.server");
    // Fail-closed: only trust cf-connecting-ip (set by Cloudflare edge, not spoofable by clients).
    const cfIp = getRequestHeader("cf-connecting-ip");
    const ip = (cfIp || "unknown").trim();
    await rateLimit(`newsletter:${ip}`, 10, 3600);

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase().trim();

    // Look up existing row.
    const { data: existing } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id, confirmed_at")
      .eq("email", email)
      .maybeSingle();

    if (existing?.confirmed_at) {
      return { success: true, message: "Ta email je že prijavljen na novice." };
    }

    // Generate token + 7-day expiry, insert or refresh.
    const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");
    const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    if (existing) {
      await supabaseAdmin
        .from("newsletter_subscribers")
        .update({ confirm_token: token, token_expires_at: expires })
        .eq("id", existing.id);
    } else {
      const { error } = await supabaseAdmin
        .from("newsletter_subscribers")
        .insert({ email, confirm_token: token, token_expires_at: expires });
      if (error && error.code !== "23505") {
        throw new Error("Napaka pri prijavi. Poskusite znova.");
      }
    }

    // Send the double-opt-in confirmation email (best-effort).
    try {
      const { enqueueTransactionalEmail } = await import("@/lib/email/enqueue-internal.server");
      await enqueueTransactionalEmail({
        templateName: "newsletter-confirm",
        recipientEmail: email,
        templateData: { confirmUrl: `${SITE_ORIGIN}/novice/potrditev?token=${token}` },
        idempotencyKey: `newsletter-confirm-${token}`,
      });
    } catch (err) {
      console.error("[newsletter] confirmation email failed", err);
    }

    return {
      success: true,
      message: "Skoraj. Preverite e-pošto in potrdite prijavo.",
    };
  });

/** Confirm a newsletter subscription using the token from the email link. */
export const confirmNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z.object({ token: z.string().min(20).max(200) }).parse(data)
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: row } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id, email, confirmed_at, token_expires_at")
      .eq("confirm_token", data.token)
      .maybeSingle();

    if (!row) {
      return { status: "invalid" as const, message: "Povezava ni veljavna ali je bila že uporabljena." };
    }
    if (row.confirmed_at) {
      return { status: "already" as const, message: "Prijava je že potrjena. Hvala!" };
    }
    if (row.token_expires_at && new Date(row.token_expires_at) < new Date()) {
      return { status: "expired" as const, message: "Povezava je potekla. Prosimo, prijavite se znova." };
    }

    await supabaseAdmin
      .from("newsletter_subscribers")
      .update({
        confirmed_at: new Date().toISOString(),
        confirm_token: null,
        token_expires_at: null,
      })
      .eq("id", row.id);

    // Add to marketing list only after explicit confirmation.
    await supabaseAdmin
      .from("marketing_recipients")
      .upsert(
        { email: row.email, label: "Prijavljeni na novice", source: "newsletter" } as never,
        { onConflict: "email,label", ignoreDuplicates: true },
      );

    return { status: "confirmed" as const, message: "Prijava potrjena. Hvala!" };
  });
